import "@/App.css";
import "@/theme-light.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import SetupBanner from "@/components/SetupBanner";
import Protected from "@/components/Protected";
import Footer from "@/components/Footer";
import ErrorBoundary from "@/components/ErrorBoundary";
import Landing from "@/pages/Landing";
import JobsFeed from "@/pages/JobsFeed";
import Contractors from "@/pages/Contractors";
import { LOADERS } from "@/lib/prefetch";

const Login = lazy(LOADERS["/login"]);
const ForgotPassword = lazy(LOADERS["/forgot-password"]);
const ResetPassword = lazy(LOADERS["/reset-password"]);
const Register = lazy(LOADERS["/register"]);
const Dashboard = lazy(LOADERS["/dashboard"]);
const PostJob = lazy(LOADERS["/jobs/new"]);
const JobDetail = lazy(LOADERS["/jobs/:id"]);
const PostAvailability = lazy(LOADERS["/availability/new"]);
const ContractorProfile = lazy(LOADERS["/contractors/:id"]);
const Bookings = lazy(LOADERS["/bookings"]);
const Profile = lazy(LOADERS["/profile"]);
const Settings = lazy(LOADERS["/settings"]);
const SecureAccount = lazy(LOADERS["/secure-account"]);
const FAQ = lazy(() => import("@/pages/FAQ"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const About = lazy(() => import("@/pages/About"));
const Admin = lazy(LOADERS["/admin"]);
const Setup = lazy(LOADERS["/setup"]);
const HowItWorks = lazy(LOADERS["/how-it-works"]);
const RateGuide = lazy(LOADERS["/rate-guide"]);
const Statements = lazy(LOADERS["/statements"]);
const Invoice = lazy(LOADERS["/invoices/:id"]);
const NotFound = lazy(LOADERS["*"]);
const PaymentSuccess = lazy(LOADERS["/payment/success"]);
const PaymentCancel = lazy(LOADERS["/payment/cancel"]);


const TOAST_OPTIONS = { style: { background: "#121212", border: "1px solid #27272A", color: "#fff", borderRadius: 0 } };
const BUILDER_ROLES = ["builder", "admin"];
const CONTRACTOR_ROLES = ["contractor", "admin"];

function PageFallback() {
  return (
    <div className="p-10 flex items-center gap-3 text-zinc-500 font-mono-data text-xs tracking-widest" data-testid="page-loading">
      <span className="w-4 h-4 border-2 border-[#FF6A00] border-t-transparent rounded-full animate-spin" /> LOADING…
    </div>
  );
}

function Shell() {
  const loc = useLocation();
  const isLanding = loc.pathname === "/";
  useEffect(() => { document.body.classList.add("theme-light"); return () => document.body.classList.remove("theme-light"); }, []);
  useEffect(() => { if (!loc.hash) window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [loc.pathname]);
  return (
    <>
      <Nav />
      <SetupBanner />
      <div className={isLanding ? "" : "bg-[#0A0A0A] min-h-[calc(100vh-56px)]"}>
        <Suspense fallback={<PageFallback />}>
          <ErrorBoundary location={loc.pathname}>
            <Outlet />
          </ErrorBoundary>
        </Suspense>
      </div>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App" data-testid="app-root">
      <AuthProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-right" toastOptions={TOAST_OPTIONS} />
          <Routes>
            <Route element={<Shell />}>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/register" element={<Register />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/about" element={<About />} />
              <Route path="/rate-guide" element={<RateGuide />} />
              <Route path="/jobs" element={<JobsFeed />} />
              <Route path="/jobs/:id" element={<JobDetail />} />
              <Route path="/contractors" element={<Contractors />} />
              <Route path="/contractors/:id" element={<ContractorProfile />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />

              <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
              <Route path="/setup" element={<Protected><Setup /></Protected>} />
              <Route path="*" element={<NotFound />} />
              <Route path="/bookings" element={<Protected><Bookings /></Protected>} />
              <Route path="/requests" element={<Navigate to="/bookings?tab=offers" replace />} />
              <Route path="/profile" element={<Protected><Profile /></Protected>} />
              <Route path="/settings" element={<Protected><Settings /></Protected>} />
              <Route path="/secure-account" element={<Protected><SecureAccount /></Protected>} />
              <Route path="/statements" element={<Protected><Statements /></Protected>} />
              <Route path="/invoices/:bookingId" element={<Protected><Invoice /></Protected>} />
              <Route path="/admin" element={<Protected><Admin /></Protected>} />
              <Route path="/jobs/new" element={<Protected roles={BUILDER_ROLES}><PostJob /></Protected>} />
              <Route path="/availability/new" element={<Protected roles={[...CONTRACTOR_ROLES, "builder"]}><PostAvailability /></Protected>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
